import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Boxes,
  ArrowRightLeft,
  Edit3,
  MapPin,
  Package,
  Loader2,
  AlertTriangle,
} from "lucide-react";
import { API } from "@/App";

export default function Inventory({ user }) {
  const [inventory, setInventory] = useState([]);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showLowStock, setShowLowStock] = useState(false);

  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [transferForm, setTransferForm] = useState({
    product_id: "",
    from_location_id: "",
    to_location_id: "",
    quantity: "",
    notes: "",
  });

  // Adjust dialog state
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const [adjustForm, setAdjustForm] = useState({
    product_id: "",
    location_id: "",
    new_quantity: "",
    reason: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchInventory();
  }, [selectedLocation, showLowStock]);

  const fetchData = async () => {
    try {
      const [locRes, prodRes] = await Promise.all([
        fetch(`${API}/locations`, { credentials: "include" }),
        fetch(`${API}/products`, { credentials: "include" }),
      ]);

      if (locRes.ok) setLocations(await locRes.json());
      if (prodRes.ok) setProducts(await prodRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchInventory = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedLocation) params.append("location_id", selectedLocation);
      if (showLowStock) params.append("low_stock", "true");

      const url = `${API}/inventory${params.toString() ? `?${params.toString()}` : ""}`;
      const response = await fetch(url, { credentials: "include" });
      if (response.ok) {
        setInventory(await response.json());
      }
    } catch (error) {
      toast.error("Failed to fetch inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();
    if (!transferForm.product_id || !transferForm.from_location_id || 
        !transferForm.to_location_id || !transferForm.quantity) {
      toast.error("Please fill all required fields");
      return;
    }

    setTransferring(true);
    try {
      const response = await fetch(`${API}/inventory/transfer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...transferForm,
          quantity: parseInt(transferForm.quantity),
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Stock transferred successfully");
        setTransferOpen(false);
        setTransferForm({
          product_id: "",
          from_location_id: "",
          to_location_id: "",
          quantity: "",
          notes: "",
        });
        fetchInventory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Transfer failed");
      }
    } catch (error) {
      toast.error("Error transferring stock");
    } finally {
      setTransferring(false);
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    if (!adjustForm.product_id || !adjustForm.location_id || 
        adjustForm.new_quantity === "" || !adjustForm.reason) {
      toast.error("Please fill all required fields");
      return;
    }

    setAdjusting(true);
    try {
      const response = await fetch(`${API}/inventory/adjust`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...adjustForm,
          new_quantity: parseInt(adjustForm.new_quantity),
        }),
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Stock adjusted successfully");
        setAdjustOpen(false);
        setAdjustForm({
          product_id: "",
          location_id: "",
          new_quantity: "",
          reason: "",
        });
        fetchInventory();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Adjustment failed");
      }
    } catch (error) {
      toast.error("Error adjusting stock");
    } finally {
      setAdjusting(false);
    }
  };

  const openAdjustForItem = (item) => {
    setAdjustForm({
      product_id: item.product_id,
      location_id: item.location_id,
      new_quantity: item.quantity.toString(),
      reason: "",
    });
    setAdjustOpen(true);
  };

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="inventory-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">
              Inventory
            </h1>
            <p className="text-muted-foreground">
              Track and manage stock across locations
            </p>
          </div>
          <div className="flex gap-2">
            <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="transfer-btn">
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Transfer
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Transfer Stock</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleTransfer} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Product *</Label>
                    <Select
                      value={transferForm.product_id}
                      onValueChange={(v) => setTransferForm({ ...transferForm, product_id: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.product_id} value={p.product_id}>
                            {p.name} ({p.sku})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>From Location *</Label>
                      <Select
                        value={transferForm.from_location_id}
                        onValueChange={(v) => setTransferForm({ ...transferForm, from_location_id: v })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="From" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.map((l) => (
                            <SelectItem key={l.location_id} value={l.location_id}>
                              {l.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>To Location *</Label>
                      <Select
                        value={transferForm.to_location_id}
                        onValueChange={(v) => setTransferForm({ ...transferForm, to_location_id: v })}
                      >
                        <SelectTrigger className="rounded-sm">
                          <SelectValue placeholder="To" />
                        </SelectTrigger>
                        <SelectContent>
                          {locations.filter((l) => l.location_id !== transferForm.from_location_id).map((l) => (
                            <SelectItem key={l.location_id} value={l.location_id}>
                              {l.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Quantity *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={transferForm.quantity}
                      onChange={(e) => setTransferForm({ ...transferForm, quantity: e.target.value })}
                      className="rounded-sm font-data"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Input
                      value={transferForm.notes}
                      onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                      className="rounded-sm"
                      placeholder="Optional notes"
                    />
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setTransferOpen(false)} className="rounded-sm">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={transferring} className="rounded-sm flex-1">
                      {transferring ? <Loader2 className="h-4 w-4 animate-spin" /> : "Transfer Stock"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>

            <Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="rounded-sm" data-testid="adjust-btn">
                  <Edit3 className="h-4 w-4 mr-2" />
                  Adjust
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Adjust Stock</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAdjust} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Product *</Label>
                    <Select
                      value={adjustForm.product_id}
                      onValueChange={(v) => setAdjustForm({ ...adjustForm, product_id: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.product_id} value={p.product_id}>
                            {p.name} ({p.sku})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location *</Label>
                    <Select
                      value={adjustForm.location_id}
                      onValueChange={(v) => setAdjustForm({ ...adjustForm, location_id: v })}
                    >
                      <SelectTrigger className="rounded-sm">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        {locations.map((l) => (
                          <SelectItem key={l.location_id} value={l.location_id}>
                            {l.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>New Quantity *</Label>
                    <Input
                      type="number"
                      min="0"
                      value={adjustForm.new_quantity}
                      onChange={(e) => setAdjustForm({ ...adjustForm, new_quantity: e.target.value })}
                      className="rounded-sm font-data"
                      placeholder="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reason *</Label>
                    <Input
                      value={adjustForm.reason}
                      onChange={(e) => setAdjustForm({ ...adjustForm, reason: e.target.value })}
                      className="rounded-sm"
                      placeholder="e.g., Cycle count, Damaged goods"
                    />
                  </div>
                  <div className="flex gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setAdjustOpen(false)} className="rounded-sm">
                      Cancel
                    </Button>
                    <Button type="submit" disabled={adjusting} className="rounded-sm flex-1">
                      {adjusting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Adjust Stock"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters */}
        <Card className="rounded-sm border">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Select value={selectedLocation || "all"} onValueChange={(v) => setSelectedLocation(v === "all" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="location-filter">
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.location_id} value={loc.location_id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant={showLowStock ? "default" : "outline"}
                onClick={() => setShowLowStock(!showLowStock)}
                className="rounded-sm"
                data-testid="low-stock-filter"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                Low Stock Only
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Inventory Table */}
        <Card className="rounded-sm border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 skeleton-pulse" />
                ))}
              </div>
            ) : inventory.length === 0 ? (
              <div className="p-8 text-center">
                <Boxes className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">
                  {showLowStock ? "No low stock items" : "No inventory found"}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead className="font-data text-right">Quantity</TableHead>
                      <TableHead className="font-data text-right">Threshold</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inventory.map((item, index) => {
                      const isLowStock = item.quantity <= (item.product?.low_stock_threshold || 10);
                      return (
                        <TableRow key={index} className="data-row">
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-sm bg-muted flex items-center justify-center">
                                <Package className="h-5 w-5 text-muted-foreground" />
                              </div>
                              <div>
                                <p className="font-medium">{item.product?.name}</p>
                                <p className="text-xs text-muted-foreground font-data">{item.product?.sku}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-muted-foreground" />
                              {item.location?.name || "Unknown"}
                            </div>
                          </TableCell>
                          <TableCell className="font-data text-right text-lg font-medium">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="font-data text-right text-muted-foreground">
                            {item.product?.low_stock_threshold || 10}
                          </TableCell>
                          <TableCell>
                            {isLowStock ? (
                              <Badge className="low-stock rounded-sm">
                                <AlertTriangle className="h-3 w-3 mr-1" />
                                Low Stock
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="rounded-sm bg-emerald-500/10 text-emerald-500">
                                In Stock
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => openAdjustForItem(item)}
                              data-testid={`adjust-item-${index}`}
                            >
                              <Edit3 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
